import type { ReactNode } from "react";

/**
 * THE SITE'S ICONS — one table, one wrapper.
 *
 * No library, and that is a decision rather than an omission. The prototype's set
 * is drawn FOR this design: another one's stroke weight and corner radii are not
 * the same, and at sixteen pixels that difference is the whole glyph. A package
 * carrying hundreds of icons for the handful used here would also be paid for by
 * participants loading the site on a venue's wifi.
 *
 * ⚠️ ONE TABLE, because three were already drifting. The footer, the funnel and the
 * organiser bar each repeated the same `24 / stroke 2 / round` envelope by hand;
 * the prototype has a single `ICO` map and a single `svgIco()`, and the next icon
 * added to one file would have come out at a different weight from the others.
 *
 * ⚠️ THE ORGANISER BAR KEEPS ITS OWN, deliberately. Its chrome is independent of
 * the site — neutral `--o-*` colours, never the client's hue — and folding its
 * icons in here would blur a separation the whole tool rests on.
 *
 * Every path is traced verbatim from the prototype. Redrawn by hand they come out
 * at a different weight and stop looking like the rest of the product.
 */
/**
 * A PIN, shared by the venue block of the footer and the "on site" answer.
 *
 * Deliberately the SAME glyph in both places: they say the same thing, and giving
 * one concept two drawings is how an interface stops being readable at a glance.
 */
const PIN = (
  <>
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
    <circle cx="12" cy="10" r="3" />
  </>
);

/**
 * UN BÂTIMENT, partagé par le bloc « Organisé par » du pied de page et la société du
 * participant.
 *
 * Volontairement le MÊME dessin : les deux désignent une personne morale, et donner
 * deux glyphes à une même notion est ce qui rend une interface illisible d'un coup
 * d'œil — exactement l'argument du repère, plus haut.
 */
/**
 * LE BÂTIMENT — la société du participant, et l'organisateur au pied de page.
 *
 * ⚠️ CE N'EST PLUS UNE MAISON, ET LE PIGNON N'ÉTAIT PAS LE SEUL PROBLÈME. Le tracé
 * d'origine dessinait un toit à deux versants : c'est le signe d'un DOMICILE, pas d'une
 * société — la notion que cette icône accompagne partout où elle sert (l'étape société,
 * le « Organisé par » du pied de page). Son défaut de rendu, un épaulement là où le
 * versant droit rejoignait son mur, n'était que ce qui l'a fait remarquer.
 *
 * Ce qui le remplace : un corps de bâtiment et son aile basse, avec trois ouvertures.
 * Les fenêtres font tout le travail — sans elles, deux volumes nus se lisent comme un
 * diagramme en barres, ce qu'a montré la comparaison des candidats.
 *
 * ⚠️ PLUS UN SEUL ANGLE ENTRE UNE DIAGONALE ET UNE VERTICALE : la silhouette est faite
 * d'horizontales, de verticales et de quarts de cercle. C'est ce qui rend le tracé
 * insensible au défaut qui a coûté deux allers-retours sur la maison. Le rayon de 1 aux
 * angles hauts reprend l'arrondi doux du reste du dessin.
 *
 * Gabarit inchangé : 24, trait 2, bouts ronds, et le même sol que les autres.
 */
const BATIMENT = (
  <path d="M4 21V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v16M14 12h4a1 1 0 0 1 1 1v8M3 21h18M8 8h2M8 12h2M8 16h2" />
);

const PATHS: Record<string, ReactNode> = {
  /* ── The funnel's answers ──
     ⚠️ ONE FAMILY, one question: BY WHAT MEANS are you present? A place, a screen,
     somebody else, or nothing. The prototype reused whatever its table held — an
     information "i" for attending in person, a FAQ "?" for declining — and those
     say nothing about the answer they sit beside.

     Each is one or two strokes with comparable ink: a dense glyph next to a sparse
     one reads as the more important choice, which is not for us to decide. */
  onsite: PIN,
  /* A screen with its stand: what a remote participant actually watches. Wifi —
     the prototype's pick — describes a connection, not an attendance. */
  remote: (
    <>
      <rect x="2" y="4" width="20" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </>
  ),
  /**
   * A person, and an arrow leaving them: DELEGATION.
   *
   * ⚠️ NOT the two-people glyph, which is what was here first. Compared at 16px —
   * the size it is actually seen at, inside its 32px tile — the second silhouette
   * dissolves into a stray mark and the whole thing reads as ONE person, which is
   * the opposite of what the answer says. Two heads at equal size read as a blob;
   * a relay of two arrows reads as "refresh". An arrow survives small sizes because
   * it is straight.
   */
  represented: (
    <>
      <circle cx="8" cy="8" r="3.4" />
      <path d="M2 19.5a6 6 0 0 1 12 0" />
      <path d="M16.5 12.5h5.5m0 0-2-2m2 2-2 2" />
    </>
  ),
  /* A struck-through circle: "not this one", read the same way everywhere. Softer
     candidates (a crossed calendar) carry more strokes for less meaning at 16px. */
  decline: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M6.2 17.8 17.8 6.2" />
    </>
  ),
  check: <path d="M20 6 9 17l-5-5" />,

  /* Le chevron d'un menu qui se déplie. Même enveloppe que le reste (24 / trait 2 /
     bouts ronds) : dessiné plus fin, il aurait sauté aux yeux comme une pièce
     rapportée à côté de la coche. */
  chevron: <path d="m6 9 6 6 6-6" />,

  /* La société du participant, à l'étape qui la lui demande. */
  company: BATIMENT,

  /* ── The footer's blocks ── */
  organiser: BATIMENT,
  dates: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  place: PIN,
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </>
  ),
  contact: (
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" />
  ),

  /* ── LA LIGNE DE SA PHOTO, EN TÊTE DE L'ALBUM (`album/Portrait.tsx`) ── */
  /**
   * LA CORBEILLE — effacer sa photo de référence.
   *
   * ⚠️ PAS LA CROIX, ET CE N'EST PAS UN CAPRICE : sur cet écran-là, `croix` FERME la
   * visionneuse (`routes/Album.tsx`). Le même dessin à deux pas de là pour « effacer
   * définitivement ma photo » ferait cliquer sur l'un en croyant faire l'autre, et
   * celui-ci est irréversible.
   *
   * ⚠️ SANS LES DEUX BARRES DU SEAU. À 16 px — la taille où elle est SERVIE — quatre
   * unités les séparent, soit deux pixels et demi : elles se bouchent et la corbeille
   * devient une tache. Le couvercle, l'anse et le seau qui se resserre suffisent à la
   * reconnaître. Gabarit de la table : 24, trait 2, encre de 4 à 20.
   */
  corbeille: (
    <path d="M4 7h16M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9.5 7V5a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v2" />
  ),
  /**
   * L'APPAREIL PHOTO — fournir SA photo, à côté de la pastille de l'album.
   *
   * ⚠️ VOLONTAIREMENT DISTINCT D'`album` : celui-ci désigne les photos de l'événement,
   * celui-là le geste d'en donner une de soi. Le viseur au centre est tout ce qui le
   * sépare d'un cadre, et c'est assez à 16 px, la taille où il est SERVI. Même gabarit
   * que le reste de la table : 24, trait 2, encre entre 2 et 22.
   */
  appareil: (
    <>
      <path d="M3 8.5A1.5 1.5 0 0 1 4.5 7h2.9l1.4-2h6.4l1.4 2h2.9A1.5 1.5 0 0 1 21 8.5v9a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 17.5z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
};

/* ── LES PAGES DU SITE ──
   Tracées telles quelles depuis la table `ICO` du prototype : c'est le même
   gabarit (24 / stroke 2 / bouts ronds), et redessinées à la main elles ne
   ressembleraient plus au reste. Les noms sont les siens, puisque le registre des
   pages les nomme. */
const PAGES: Record<string, ReactNode> = {
  programme: (
    <>
      <rect x="3" y="4" width="18" height="17" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </>
  ),
  badge: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 9h4M7 13h7" />
    </>
  ),
  /**
   * REGISTRATION — a filled-in form, not a badge.
   *
   * ⚠️ IT CARRIED THE BADGE'S ICON, so the home screen showed two neighbouring tiles
   * with the same drawing: "Inscription" and "Badge" were told apart by their word
   * alone. Yet they are the participant's two moves, and the second only exists after
   * the first.
   *
   * The ticked clipboard carries over the product's intent — V1 puts `AuditOutlined` (a
   * validated document) on its "Mon inscription" tile. The silhouette is deliberately
   * far from `fichier` (folded-corner document) and from `badge` (a card on its side):
   * at 17px on a tile it is the outline that gets recognised, not the detail.
   */
  inscription: (
    <>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="m9 14 2 2 4-4" />
    </>
  ),
  /**
   * DES LIGNES DE TEXTE — une section, c'est le contenu de l'organisateur.
   *
   * ⚠️ PLUS LE « i » DANS UN ROND. Ce dessin dit « information », un mot que la page
   * ne porte plus depuis qu'elle s'appelle « Section » ; et il partageait sa
   * silhouette avec le globe de `monde` (deux cercles de rayon 9), à quelques pixels
   * de là dans un même écran. Le bloc de texte de la table partagée dit ce que la
   * page CONTIENT, et il ne ressemble à aucune autre tuile : elles sont toutes
   * bâties sur un rectangle.
   *
   * Trois traits peuvent se lire « ouvrir le menu » : vérifié, le produit n'a AUCUN
   * bouton de ce genre, ni ici ni dans la barre. Et le mot est toujours à côté, sous
   * la tuile comme dans le menu.
   */
  infos: <path d="M21 6H3M15 12H3M17 18H3" />,
  /**
   * UNE CASE COCHÉE ET DES LIGNES — des questions, et les réponses qu'on y met.
   *
   * ⚠️ PAS LE PRESSE-PAPIERS D'`inscription`, QUI PORTE DÉJÀ UNE COCHE : les deux
   * peuvent voisiner sur l'accueil, et à 17 px c'est la SILHOUETTE qui se reconnaît,
   * pas le détail. Celle-ci est ouverte (un carré en haut à gauche, des lignes à
   * droite), celle-là est un rectangle plein debout. Écarté aussi le document ligné,
   * trop proche du bloc de texte de `infos` : deux pages voisines dans le même menu.
   */
  formulaire: (
    <>
      <rect x="3" y="5" width="6" height="6" rx="1" />
      <path d="m3 17 2 2 4-4" />
      <path d="M13 6h8M13 12h8M13 18h8" />
    </>
  ),
  /**
   * LE GLOBE : la langue servie. Le dessin du prototype (`.se-alt`), à l'encre
   * courante : il accompagne l'avertissement de repli de langue, il ne le décore pas.
   */
  monde: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18" />
    </>
  ),
  /**
   * LA PERSONNE — vos réponses, votre fiche.
   *
   * ⚠️ PAS LE PRESSE-PAPIERS D'`inscription`, ET C'EST UNE MESURE À L'ŒIL QUI L'A DÉCIDÉ :
   * l'accueil montrait « Inscription », « Mes informations » et « Vos préférences » côte
   * à côte AVEC LE MÊME DESSIN. Le registre des pages porte déjà cette leçon, mot pour
   * mot, pour le badge. Même géométrie que `partenaires`, dont ce n'est que la version
   * à une silhouette : les deux se lisent alors comme une famille, l'une « moi »,
   * l'autre « moi et les miens ».
   */
  profil: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  partenaires: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    </>
  ),
  live: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M10 9l5 3-5 3z" />
    </>
  ),
  scan: (
    <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10" />
  ),
  trophee: (
    <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0zM7 5H4v2a3 3 0 0 0 3 3M17 5h3v2a3 3 0 0 1-3 3" />
  ),
  album: (
    <>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="2" />
      <path d="M3 17l5-4 4 3 4-4 5 5" />
    </>
  ),
  fichier: (
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
      <path d="M14 3v6h6" />
    </>
  ),
  replay: <path d="M3 12a9 9 0 1 0 3-6.7L3 8m0-5v5h5" />,
  etoile: (
    <path d="M12 3l2.7 5.8 6.3.8-4.6 4.3 1.2 6.1-5.6-3.1-5.6 3.1 1.2-6.1L3 9.6l6.3-.8z" />
  ),
  accueil: (
    <>
      <path d="M3 11l9-8 9 8" />
      <path d="M5 10v10h14V10" />
    </>
  ),
  /* Le débord de la barre du bas, sur mobile. */
  plus: (
    <>
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </>
  ),
  /**
   * LA CROIX — retirer ce qui est sur cette ligne.
   *
   * ⚠️ ELLE NE DIT PAS « FERMER » ICI, et c'est le voisinage qui tranche : sur une ligne
   * qui porte déjà un rang, un nom et un interrupteur, elle ne peut désigner que la
   * ligne. La porte de `sortie` disait « s'en aller » — juste pour une déconnexion, faux
   * pour une étape qu'on retire du parcours.
   *
   * Deux traits de 6 à 18 : l'encre reste entre 2 et 22 comme partout dans cette table,
   * et à 14 px — la taille où elle est SERVIE — les bras font 7 px, assez pour se lire.
   */
  croix: (
    <>
      <path d="m6 6 12 12" />
      <path d="m18 6-12 12" />
    </>
  ),
  /**
   * LA SORTIE — se déconnecter.
   *
   * Une porte et une flèche qui s'en va : le geste, pas l'objet. Une croix aurait dit
   * « fermer », et c'est ce que fait le bouton d'à côté dans la plupart des menus.
   */
  /**
   * ⚠️ UNE PORTE PLEINE, ET C'EST UNE MESURE QUI L'A DÉCIDÉ. Le tracé d'origine — celui
   * de la famille, trois traits ouverts — pesait 19,4 % d'encre là où cette table tient
   * entre 24,5 et 31 % (`fichier`, `badge`, `inscription`, `verrou`). À côté du
   * presse-papiers de « Mon inscription », il paraissait plus petit et plus pâle alors
   * que sa boîte est la même. Panneau fermé : 24,3 %, dans la bande.
   *
   * Comptée en pixels sombres sur un rendu à 96 px, pas à l'œil — l'œil disait d'abord
   * que c'était le presse-papiers qui était trop lourd, et il avait tort.
   */
  sortie: (
    <>
      <rect x="3" y="3" width="10" height="18" rx="2" />
      <path d="m17 16 4-4-4-4" />
      <path d="M21 12h-8" />
    </>
  ),
  /**
   * THE PADLOCK — what has not been issued yet.
   *
   * A CLOSED padlock, and that is the right sign: what it accompanies opens through a
   * move by the participant — registering, paying — not through a permission being
   * withheld from them.
   *
   * ⚠️ NOT THE PROTOTYPE'S PATH, AND THAT IS A DELIBERATE DEPARTURE. Its body spans 14
   * units out of 24: at 200px it is impeccable, at 16px — the size it is ACTUALLY served
   * at — its gaps close up and it reads as a smudge.
   *
   * ⚠️ AND IT THEN HAD TO BE PULLED BACK IN. My first correction took it to 18 units
   * wide, which put its ink at 1 to 23 out of 24 — GRAZING the edges of its box, where
   * every other icon in this table sits between 2 and 22. At 16px those two thirds of a
   * pixel read as a clipped drawing. Measured: 18 × 20 of ink for 23.3%, which is
   * `fichier`'s box exactly.
   */
  verrou: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
};

Object.assign(PATHS, PAGES);

/** The names the table knows — a typo is caught at build time, not on screen. */
export type IconName = keyof typeof PATHS;

/**
 * `aria-hidden` on every one of them: each is beside a label that says the same
 * thing. Announced as well, a screen reader would hear it twice.
 *
 * Size and colour come from CSS (`currentColor`, and the parent's `svg` rule), so
 * a caller never has to state either.
 */
export function Icon({ name }: { name: IconName | string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {PATHS[name] ?? PATHS.onsite}
    </svg>
  );
}
